import clsx from "clsx";
import { debounce } from "lodash-es";
import { memo, use, useCallback, useEffect, useMemo, useState } from "react";

import { SpellCheckContext } from "../../../providers/spell-check-context";
import { APITranscriptToken, Stage } from "../../../types/types";
import { getSpeakerColor } from "../../../utils/get-speaker-color";
import { hexToRGBA } from "../../../utils/hex-to-rgba";
import { toTranscriptPath } from "../../../utils/transcript-utils";
import { TokenPopover } from "./token-popover";

interface TranscriptTokenProps {
  stage: Stage;
  token: APITranscriptToken;
  sliceIndex: number;
  tokenIndex: number;
  current?: boolean;
  color?: string;
  onClick?: (token: APITranscriptToken) => void;
}

function TranscriptTokenComponent(props: TranscriptTokenProps) {
  const {
    token,
    sliceIndex,
    tokenIndex,
    color = getSpeakerColor(0),
    current,
    onClick,
    stage,
  } = props;
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [isSpellingError, setIsSpellingError] = useState(false);
  const { check } = use(SpellCheckContext);

  const checkSpelling = useCallback(
    (word = "") => {
      setIsSpellingError(!check(word));
    },
    [check],
  );

  const checkSpellingDebounced = useMemo(
    () => debounce(checkSpelling, 2000),
    [checkSpelling],
  );

  useEffect(() => {
    checkSpellingDebounced(token.transcript);
  }, [checkSpellingDebounced, token.transcript]);

  return (
    <span
      data-type="token"
      data-start-ms={token.startMs}
      data-path={toTranscriptPath(sliceIndex, tokenIndex)}
      className={clsx(
        /* Without this position rule, rendering large transcripts becomes
         * unacceptably slow on mouseover due to the browser's collision-detection */
        "relative",
        "border-b-2 border-transparent",
        isSpellingError &&
          stage === "POST_TRANSCRIPTION" &&
          "underline decoration-red-600 decoration-wavy",
      )}
      style={{
        ...(current && {
          background: hexToRGBA(color, 0.3),
          borderBottomColor: hexToRGBA(color),
        }),
      }}
      onClick={() => {
        onClick?.(token);
      }}
      onContextMenu={(e) => {
        if (isSpellingError) {
          e.preventDefault();
          setPopoverOpen((prev) => !prev);
        }
      }}
      onKeyPress={(e) => e.key === "Enter" && onClick?.(token)}
    >
      {token.transcript}
      {popoverOpen && isSpellingError && (
        <TokenPopover
          sliceIndex={sliceIndex}
          tokenIndex={tokenIndex}
          token={token}
          onClose={() => {
            setPopoverOpen(false);
            checkSpelling(token.transcript);
          }}
        />
      )}
    </span>
  );
}

export const TranscriptToken = memo(TranscriptTokenComponent);
